import {
  formatDate,
  formatTimeDistance,
} from 'src/modules/shared/utils/formatting/date.util';

/**
 * Formats an integration timestamp in a user-friendly way.
 *
 * Uses an absolute timestamp plus a relative distance label when possible.
 */
export const formatIntegrationDateTime = (
  value: string | null | undefined,
  params: { locale: string; notAvailableLabel: string }
): string => {
  if (!value) return params.notAvailableLabel;

  const absolute = formatDate(value);
  if (!absolute) return params.notAvailableLabel;

  const date = new Date(value);
  const relative = Number.isFinite(date.getTime())
    ? formatTimeDistance(date, true, params.locale)
    : '';

  return relative ? `${absolute} (${relative})` : absolute;
};

/**
 * Formats the "next sync" timestamp.
 *
 * @remarks
 * When the scheduled time is slightly in the past (common with periodic schedulers),
 * show a friendlier "starting soon" label instead of "X minutes ago".
 */
export const formatIntegrationNextSyncDateTime = (
  value: string | null | undefined,
  params: {
    locale: string;
    notAvailableLabel: string;
    startingSoonLabel: string;
    now?: Date;
    startingSoonGraceMinutes?: number;
  }
): string => {
  if (!value) return params.notAvailableLabel;

  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return params.notAvailableLabel;

  const now = params.now ?? new Date();
  const graceMinutes = params.startingSoonGraceMinutes ?? 15;
  const graceMs = graceMinutes * 60_000;

  if (
    date.getTime() <= now.getTime() &&
    now.getTime() - date.getTime() <= graceMs
  ) {
    return params.startingSoonLabel;
  }

  return formatIntegrationDateTime(value, {
    locale: params.locale,
    notAvailableLabel: params.notAvailableLabel,
  });
};
